import { IonButton, IonContent, IonPage, IonSpinner, IonTextarea, useIonToast } from "@ionic/react";
import { arrowBackOutline } from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import Header from "src/components/Header";
import { campoEstilo, Etiqueta } from "src/components/SolicitudCapacitacionCampos";
import SolicitudSstFormulario, { ValoresIniciales } from "src/components/SolicitudSstFormulario";
import { EstadoSolicitudSstChip } from "src/pages/SolicitudesSst";
import { SolicitudSstAPI, SolicitudSstDetalle as Detalle, TipoSolicitudSst } from "src/services/sst";

const VOLVER = "/sst/solicitudes?tab=mis";

const caja: React.CSSProperties = {
  background: "#f5f5f7",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 14,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

/**
 * Detalle de una Solicitud a SST propia (/sst/solicitudes/:id). Si SST la
 * devolvió, muestra el comentario y el formulario precargado para corregirla y
 * reenviarla; mientras esté abierta, se puede cancelar.
 */
const SolicitudSstDetalle: React.FC = () => {
  const [present] = useIonToast();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const [s, setS] = useState<Detalle | null>(null);
  const [tipo, setTipo] = useState<TipoSolicitudSst | null>(null);
  const [cargando, setCargando] = useState(true);
  const [cancelando, setCancelando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [ocupado, setOcupado] = useState(false);

  const avisar = useCallback(
    (message: string, color = "warning") => present({ message, duration: 3500, color }),
    [present]
  );

  useEffect(() => {
    const solicitudId = Number(id);
    if (!solicitudId) return;
    setCargando(true);
    Promise.all([SolicitudSstAPI.detalle(solicitudId), SolicitudSstAPI.catalogo()])
      .then(([rd, rc]) => {
        const d = rd.data.data;
        setS(d);
        setTipo((rc.data.data.tipos || []).find((t) => t.codigo === d.tipo) || null);
      })
      .catch((err) => avisar(err?.response?.data?.error || "No se pudo obtener la solicitud", "danger"))
      .finally(() => setCargando(false));
  }, [id, avisar]);

  const inicial = useMemo<ValoresIniciales | undefined>(() => {
    if (!s) return undefined;
    const v = s.valores || {};
    return {
      valores: v,
      ps: v.punto_servicio ? { id: v.punto_servicio, codigo: v.ps_codigo || "", nombre: s.ps_nombre || "" } : null,
    };
  }, [s]);

  const reenviar = async (payload: Record<string, any>) => {
    if (!s) return false;
    try {
      const r = await SolicitudSstAPI.reenviar(s.id, payload);
      avisar(r.data.data?.info || "Solicitud reenviada a SST", "success");
      history.replace(VOLVER);
      return true;
    } catch (err: any) {
      avisar(err?.response?.data?.error || "No se pudo reenviar la solicitud", "danger");
      return false;
    }
  };

  const cancelar = async () => {
    if (!s) return;
    if (!motivo.trim()) {
      avisar("Indicá el motivo de la cancelación");
      return;
    }
    setOcupado(true);
    try {
      const r = await SolicitudSstAPI.cancelar(s.id, motivo.trim());
      avisar(r.data.data?.info || "Solicitud cancelada", "success");
      history.replace(VOLVER);
    } catch (err: any) {
      avisar(err?.response?.data?.error || "No se pudo cancelar la solicitud", "danger");
      setOcupado(false);
    }
  };

  return (
    <IonPage>
      <Header titulo={`Solicitud a SST N° ${id}`} botonIzq={{ icono: arrowBackOutline, url: VOLVER }} />
      <IonContent id="sst-solicitud-detalle">
        <div style={{ padding: 16, maxWidth: 800, margin: "0 auto", boxSizing: "border-box" }}>
          {cargando ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <IonSpinner />
            </div>
          ) : !s ? (
            <p style={{ color: "#777" }}>La solicitud no existe.</p>
          ) : (
            <>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                <EstadoSolicitudSstChip estado={s.estado} />
                <strong style={{ fontSize: 14 }}>{s.tipo_label}</strong>
              </div>

              {s.comentario_sst && (
                <div
                  style={{
                    background: s.estado === "APROBADA" ? "#eaf7ef" : s.estado === "RECHAZADA" ? "#fdecea" : "#f4ecf8",
                    borderLeft: `4px solid ${
                      s.estado === "APROBADA" ? "#27ae60" : s.estado === "RECHAZADA" ? "#c0392b" : "#8e44ad"
                    }`,
                    borderRadius: 6,
                    padding: "10px 12px",
                    marginBottom: 16,
                    fontSize: 14,
                  }}
                >
                  <strong>Comentario de SST</strong>
                  <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{s.comentario_sst}</div>
                  {s.gestionado_por && <small style={{ color: "#777" }}>{s.gestionado_por}</small>}
                </div>
              )}

              {s.puede_corregir && tipo ? (
                <>
                  <p style={{ fontSize: 13, color: "#555" }}>Corregí lo que te pidió SST y reenviala.</p>
                  <SolicitudSstFormulario
                    tipo={tipo}
                    inicial={inicial}
                    textoBoton="Corregir y reenviar"
                    onEnviar={reenviar}
                    onError={(m) => avisar(m)}
                  />
                </>
              ) : (
                s.campos.map((c) => (
                  <div key={c.etiqueta} style={campoEstilo}>
                    <Etiqueta texto={c.etiqueta} />
                    <div style={caja}>{c.valor || "-"}</div>
                  </div>
                ))
              )}

              {s.estado === "CANCELADA" && s.motivo_cancelacion && (
                <p style={{ fontSize: 13, color: "#555" }}>Cancelada: {s.motivo_cancelacion}</p>
              )}

              {s.puede_cancelar && (
                <div style={{ marginTop: 24, borderTop: "1px solid #eee", paddingTop: 12 }}>
                  {!cancelando ? (
                    <IonButton fill="clear" color="danger" size="small" onClick={() => setCancelando(true)}>
                      Cancelar solicitud
                    </IonButton>
                  ) : (
                    <>
                      <Etiqueta texto="Motivo de la cancelación" obligatorio />
                      <IonTextarea
                        value={motivo}
                        onIonChange={(e) => setMotivo(e.detail.value || "")}
                        rows={2}
                        maxlength={1000}
                        style={{ border: "1px solid #ccc", borderRadius: 4, padding: "0 8px" }}
                        disabled={ocupado}
                      />
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <IonButton color="danger" size="small" disabled={ocupado} onClick={cancelar}>
                          Confirmar cancelación
                        </IonButton>
                        <IonButton fill="outline" size="small" disabled={ocupado} onClick={() => setCancelando(false)}>
                          Volver
                        </IonButton>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SolicitudSstDetalle;
